import React, {useEffect, useRef, useState} from 'react';
import {
  View,
  StyleSheet,
  Animated,
  LayoutChangeEvent,
  Text,
  TextStyle,
} from 'react-native';
import {useTheme} from '../theme/BrandThemeProvider';
import {tokens} from '../theme/tokens';
import Styles from 'src/components/Styles';
import {stepToPercent} from '../model/steps';
import {
  PROGRESS_DOT_SIZE as DOT,
  PROGRESS_FIXED_WIDTH as FIXED_WIDTH,
  PROGRESS_RAIL_HEIGHT as RAIL,
} from 'src/components/elements/activityTracker/utils/constants';

type Props = {
  currentStep: 1 | 2 | 3;
  totalSteps?: 3;
  labels: [string, string, string];
};

export const ProgressRail: React.FC<Props> = ({
  currentStep,
  totalSteps = 3,
  labels,
}) => {
  const theme = useTheme();
  const [w, setW] = useState(0);
  const progress = useRef(new Animated.Value(0)).current;

  const onLayoutTrack = (e: LayoutChangeEvent) => {
    setW(e.nativeEvent.layout.width);
  };

  useEffect(() => {
    const p = stepToPercent(currentStep, totalSteps); // 0, 0.5, 1
    Animated.spring(progress, {
      toValue: p * (w || 1),
      useNativeDriver: false,
      tension: 120,
      friction: 18,
    }).start();
  }, [currentStep, totalSteps, w, progress]);

  const xAt = (i: number) => (i / (totalSteps - 1)) * w;

  const labelW = Math.min(
    tokens.progress.labelMaxWidth,
    Math.max(tokens.progress.labelMinWidth, w / 2),
  );

  return (
    <View style={s.wrap} pointerEvents="none">
      <View style={s.track} onLayout={onLayoutTrack}>
        <View style={[s.rail, {backgroundColor: theme.secondary}]}>
          <Animated.View
            style={[s.fill, {backgroundColor: theme.primary, width: progress}]}
          />
        </View>

        {w > 0 && (
          <View style={[s.dotsLayer, {width: w}]}>
            {[0, 1, 2].map((i) => {
              const isCompleted = i + 1 < currentStep;
              const isActive = i + 1 === currentStep;
              const bg =
                isCompleted || isActive ? theme.primary : Styles.neutralHues;
              const border =
                isCompleted || isActive ? theme.primary : Styles.neutralHues;
              return (
                <View
                  key={`dot-${i}`}
                  style={[
                    s.dot,
                    {
                      left: xAt(i) - DOT / 2,
                      backgroundColor: bg,
                      borderColor: border,
                    },
                  ]}
                />
              );
            })}
          </View>
        )}
      </View>

      <View style={{height: tokens.spacing.xs + DOT / 2}} />

      {w > 0 && (
        <View style={[s.labelsLayer, {width: w}]}>
          {[0, 1, 2].map((i) => {
            const color = theme.text.primary;
            const left = xAt(i);
            return (
              <View
                key={`label-${i}`}
                style={[
                  s.labelBox,
                  s.labelBoxCenter,
                  {left, width: labelW, transform: [{translateX: -labelW / 2}]},
                ]}
              >
                <Text
                  numberOfLines={2}
                  allowFontScaling
                  adjustsFontSizeToFit
                  minimumFontScale={tokens.progress.labelMinFontScale}
                  style={[s.label, {color}]}
                >
                  {labels[i]}
                </Text>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};

const s = StyleSheet.create({
  wrap: {width: '100%', position: 'relative', alignItems: 'center'},
  track: {
    width: FIXED_WIDTH,
    alignSelf: 'center',
    position: 'relative',
    marginHorizontal: tokens.spacing.sm,
  },
  rail: {
    width: '100%',
    height: RAIL,
    borderRadius: RAIL / 2,
    overflow: 'hidden',
    backgroundColor: Styles.neutralHues,
  },
  fill: {height: '100%', borderRadius: RAIL / 2},
  dotsLayer: {
    position: 'absolute',
    height: DOT,
    left: 0,
    top: -(DOT - RAIL) / 2,
  },
  dot: {
    position: 'absolute',
    width: DOT,
    height: DOT,
    borderRadius: DOT / 2,
    borderWidth: 2,
  },
  labelsLayer: {
    position: 'relative',
    alignSelf: 'center',
    height: tokens.font.label.size * 2 + tokens.spacing.sm,
    overflow: 'visible',
  },
  labelBox: {
    position: 'absolute',
  },
  labelBoxCenter: {
    alignItems: 'center',
  },
  label: {
    fontSize: 14,
    fontWeight: '400' as TextStyle['fontWeight'],
    textAlign: 'center',
    fontFamily: 'Aeonik',
    color: '#111',
  },
});

export default ProgressRail;
